import type { MiniGame, MiniHost } from '@/minigames/types';
import type { Game } from '@/core/game';
import { div, btn, sideBlock, shuffled } from '@/minigames/kit';
import { tr } from '@/i18n';

/**
 * v1.0 新手教學：4F 人資 Grace 的「新人教育訓練」（開在作業視窗裡，一樣要按 ✕ 關）
 * 一頁一個主題：進廠動線 → 衛生分區（地板顏色）→ 服裝（制服、帽子、圍裙）→ 工安 → 你的工作（依職務）→ 小測驗
 */
export const APRON = (): Record<string, { name: string; color: string; who: string }> => ({
  disposable: { name: tr("拋棄式圍裙（生食）"), color: '#e9f1f3', who: tr("調理區生食人員（處理生肉、裹粉、投料）") },
  skyblue: { name: tr("天藍色可重複使用圍裙（熟食）"), color: '#7cc4ea', who: tr("熟食人員、熟食收料、熟切、煮飯區") },
  graydark: { name: tr("灰黑色可重複使用圍裙"), color: '#3b4145', who: tr("前置區、1F 洗籃區、2F 熟食洗籃區") },
});

export function trainingGame(g: Game): MiniGame {
  return {
    id: 'training',
    mount(host: MiniHost) {
      const c = g.c; const role = c.roleById.get(g.day.roleId)!;
      const A = APRON(); const myApron = role.apron ? A[role.apron] : null;
      const hat = c.rules.hats.find(x => x.hat === role.hat);
      const jobs = c.missions.filter(m => m.steps.some(s => s.do === 'minigame') && m.for?.roles?.includes(role.id));
      const where = (m: typeof jobs[number]) => { const s = m.steps.find(x => x.do === 'minigame')!; const e = c.floorById.get(s.floor)?.equipment.find(q => q.id === s.target); return `${s.floor}・${e?.name.split(tr("（"))[0].trim() ?? s.target}`; };
      const zones = c.rules.zones;
      type Page = { title: string; body: (HTMLElement | string)[] };
      const ul = (items: string[]) => { const u = document.createElement('ul'); u.className = 'mg-list'; for (const t of items) { const li = document.createElement('li'); li.textContent = t; u.append(li); } return u; };
      const pages: Page[] = [
        { title: tr("歡迎加入！"), body: [
          div('tr-big', {}, tr("{0}・{1}", role.deptName, role.name)),
          tr("今天先上新人教育訓練。上完再去工作（開始時間 {0}，地點 {1}）。", role.shift.start, role.startFloor),
          div('tr-note', {}, role.description)] },
        { title: tr("① 進廠與動線"), body: [ul([
          tr("上班、下班都在 1F 廠區外的打卡鐘打卡。"),
          tr("要進生產區：更衣室換工作服 → 氣浴室（張開手轉一圈）→ 洗手區：洗手、酒精、烘手 → 推門進去。"),
          tr("沒過氣浴室、沒洗手也推得開門，但算違規：被品保、主管看到會記缺失、扣信任。"),
          tr("往更乾淨的區域要走單向門，回不去；請同事從裡面偷開門也是違規。"),
          tr("下班前沒做完自己的工作就打卡，會被主管罵。")])] },
        { title: tr("② 衛生分區（看地板顏色）"), body: [div('tr-zones', {}, ...zones.map(z => div('tr-zone', {}, div('tr-swatch', { background: z.color }), div('', {}, div('', { fontWeight: '800' }, z.name), div('tr-small', {}, z.description)))))] },
        { title: tr("③ 服裝：制服、帽子、圍裙"), body: [
          ul(c.rules.uniforms.map(u => tr("{0}：{1}", u.uniform, u.dept))),
          ul(c.rules.hats.map(x => tr("{0}＝{1}", x.label, x.meaning))),
          div('tr-aprons', {}, ...Object.values(A).map(a => div('tr-apron', {}, div('tr-swatch', { background: a.color }), div('', {}, div('', { fontWeight: '800' }, a.name), div('tr-small', {}, a.who))))),
          div('tr-mine', {}, tr("你的服裝：{0}；帽子：{1}；圍裙：{2}。", c.rules.uniforms.find(u => u.uniform.includes(role.uniform === 'green' ? '草綠' : role.uniform === 'gray' ? '灰' : '白'))?.uniform ?? tr("全白"), hat ? hat.label : tr("一般髮帽（新人加黃圈）"), myApron ? myApron.name : tr("不用穿圍裙"))),
          tr("飾品、手錶都放進置物櫃；口罩遮住口鼻，髮帽完全包住頭髮。")] },
        { title: tr("④ 工安"), body: [ul([
          tr("機台運轉中絕對不能伸手（絞末機、切菜機、輸送帶）。要排除卡料先停機、斷電。"),
          tr("刀具用完放回刀架；切的時候手指彎起來（貓爪）。"),
          tr("烏龜車＋籃子最多疊 5 個，超過 4 個會擋住視線；食材籃不能直接放地上。"),
          tr("冷凍庫裡面有釋放裝置和求救鈴；不要一個人待太久。"),
          tr("地板濕滑處慢慢走；油炸機、蒸汽鍋很燙。"),
          tr("受傷了要通報主管；遊戲裡可以說「{0}」回到事故前。", c.game.rewind.phrase)])] },
        { title: tr("⑤ 你的工作"), body: [
          div('tr-big', {}, role.name), tr("權限：{0}", role.authority), ul(role.notes),
          ...(jobs.length ? [div('tr-sub', {}, tr("你的作業（每天會抽一個當主線）：")), ul(jobs.map(m => tr("{0} {1}（{2}）", m.icon, m.title, where(m))))] : []),
          tr("別的職務的工作也可以幫忙（在工作站按 E，或 ☰ →「🎮 工作一覽」），會算成支線任務。")] },
      ];
      // 小測驗
      const qs: { q: string; opts: [string, boolean][] }[] = [
        { q: tr("要進生產區，正確的順序是？"), opts: [[tr("換工作服 → 氣浴室 → 洗手 → 進去"), true], [tr("洗手 → 換工作服 → 直接進去"), false], [tr("穿著工作服就可以直接進去"), false]] },
        myApron ? { q: tr("你的職務要穿哪一種圍裙？"), opts: Object.entries(A).map(([k, a]) => [a.name, k === role.apron] as [string, boolean]) }
          : { q: tr("帽子的左右兩邊各有一個藍色「＝」的是？"), opts: [[tr("副理"), true], [tr("課長"), false], [tr("新人"), false]] },
        { q: tr("食材的籃子可以直接放在地上嗎？"), opts: [[tr("不行，下面一定要有烏龜車"), true], [tr("可以，地板很乾淨"), false], [tr("只有趕時間的時候可以"), false]] },
      ];
      const content = div('tr-page'); const nav = div('tr-nav');
      host.stage.append(div('tr-wrap', {}, content, nav));
      const toc = div('');
      host.side.append(sideBlock(tr("課程"), toc), sideBlock(tr("講師"), div('', {}, tr("人資 Grace：有問題隨時問！上完課再去換工作服。"))));
      let p = 0; let correct = 0;
      const draw = () => {
        toc.textContent = '';
        [...pages.map(x => x.title), tr("⑥ 小測驗")].forEach((t, i) => toc.append(div('tr-toc' + (i === p ? ' now' : i < p ? ' done' : ''), {}, `${i < p ? '✔ ' : ''}${t}`)));
        host.status(tr("第 {0}／{1} 頁", p + 1, pages.length + 1));
        content.textContent = ''; nav.textContent = '';
        if (p < pages.length) {
          content.append(div('tr-title', {}, pages[p].title), ...pages[p].body.map(b => typeof b === 'string' ? div('tr-p', {}, b) : b));
          if (p > 0) nav.append(btn(tr("← 上一頁"), () => { p--; draw(); }));
          nav.append(btn(p === pages.length - 1 ? tr("去小測驗 →") : tr("下一頁 →"), () => { p++; draw(); }, 'mg-btn primary'));
          return;
        }
        quiz(0);
      };
      const quiz = (i: number) => {
        content.textContent = ''; nav.textContent = '';
        if (i >= qs.length) {
          host.finish({ ok: true, score: correct / qs.length, summary: tr("教育訓練完成！答對 {0}／{1} 題。", correct, qs.length), achievement: tr("新人教育訓練結業"),
            lines: [tr("接下來：去更衣室換 {0} 工作服，開始今天的工作。", role.uniform === 'office' ? tr("（辦公室不用換，進現場才換）") : '')] });
          return;
        }
        const q = qs[i]; let tried = false;
        content.append(div('tr-title', {}, tr("小測驗 {0}／{1}", i + 1, qs.length)), div('tr-q', {}, q.q));
        for (const [t, good] of shuffled(host.rng, q.opts)) {
          const b = btn(t, () => {
            if (good) { if (!tried) correct++; host.sfx('ok'); host.flash(tr("答對了！"), 'good'); host.after(500, () => quiz(i + 1)); }
            else { tried = true; host.sfx('warn'); host.flash(tr("再想想看～"), 'bad'); b.disabled = true; }
          }, 'mg-btn tr-opt');
          content.append(b);
        }
      };
      draw();
    },
  };
}
